/**
 * Identity-preserving merge of a freshly-read message list into the previous
 * render's list. IndexedDB hands back brand-new objects on every `getAll`, so
 * `setMessages(fresh)` re-renders the ENTIRE chat list on every reload — every
 * save, every cross-tab broadcast — even when nothing visible changed.
 *
 * This matches each incoming row to the previous one by stable `id`, reuses the
 * previous object reference when the row's `updatedAt` is unchanged, and returns
 * the SAME array reference when nothing changed at all. Combined with
 * `React.memo(ChatMessage)` and `id`-keyed rows, React then bails out of
 * re-rendering the untouched messages. Requires every write path to stamp
 * `updatedAt` so a genuine content change is never mistaken for "unchanged".
 *
 * Pure and dependency-free so it can be unit-tested without the hook's heavy
 * data-sources / storage imports.
 */
export const reconcileMessages = (prev, incoming) => {
  const prevById = new Map(prev.map((msg) => [msg.id, msg]));
  let changed = incoming.length !== prev.length;
  const next = incoming.map((inc, index) => {
    const old = prevById.get(inc.id);
    if (old && old.updatedAt === inc.updatedAt) {
      // Same content; reuse the prior reference. Still flag a change if the row
      // moved position, so React re-keys correctly.
      if (old !== prev[index]) changed = true;
      return old;
    }
    changed = true;
    return inc;
  });
  return changed ? next : prev;
};
